import { Autocomplete, Box, Card, CardContent, Chip, FormControl, Stack, Typography, TextField } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { IFileUploadMapped, IFileUploadMappedOptions, IDataImportConstants } from '../../../../shared/services/file-upload/file-upload.model'
import { CheckCircleOutlineOutlined } from '@mui/icons-material'
import { MappingSelection, createConstantSelection, createHeaderSelection, isCompositeSelection, isConstantSelection, isHeaderSelection } from '../../../../shared/utils/mapping-selection.utils'
import { TooltipButton } from '../../miscellaneous-components/tooltip-button'

export const DataImportMappingTile: FunctionComponent<{
  header: string,
  fileUpload: IFileUploadMapped,
  value: MappingSelection[] | MappingSelection | undefined,
  handleChange: (header: string, value: MappingSelection[]) => void,
  selectedValues: { [header: string]: MappingSelection[] }
  optionsOverride?: IFileUploadMappedOptions[],
  multiple?: boolean,
}> = ({ header, fileUpload, value, handleChange, selectedValues, optionsOverride, multiple = true }) => {

  const options: IFileUploadMappedOptions[] = (optionsOverride ?? ((fileUpload as any).options as IFileUploadMappedOptions[])) || [];
  const selections = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value]
  );
  //State
  const selectedSingleValue = !multiple ? (selections[0] as MappingSelection | undefined) : undefined;
  const [constantText, setConstantText] = useState<string>('')
  const [constantDate, setConstantDate] = useState<any>(null)
  const [localConstSelected, setLocalConstSelected] = useState<boolean>(false)
  //Varaibles
  const headerKey = String(header).toLowerCase()
  const normalizedHeaderKey = headerKey.replace(/[^a-z0-9]/g, '')
  const constants = fileUpload.constants
  const providedConstantOptions = useMemo(() => {
    if (!constants) return undefined
    const directMatch = constants[header as keyof IDataImportConstants]
    if (directMatch) return directMatch
    const lowerHeader = String(header).toLowerCase()
    const matchEntry = Object.entries(constants as Record<string, string[] | undefined>).find(([key]) => {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '')
      return normalizedKey === normalizedHeaderKey || key.toLowerCase() === lowerHeader
    })
    return matchEntry?.[1]
  }, [constants, header, normalizedHeaderKey])

  const isDateHeader = useMemo(() => headerKey.includes('date'), [headerKey])

  const isConstantSelected = useMemo(() => {
    if (multiple) return false
    return localConstSelected || isConstantSelection(selectedSingleValue)
  }, [multiple, selectedSingleValue, localConstSelected])

  const isCompositeMapped = useMemo(() => !multiple && isCompositeSelection(selectedSingleValue), [multiple, selectedSingleValue])

  useEffect(() => {
    if (!isConstantSelected) {
      setConstantText('')
      setConstantDate(null)
      return
    }
    if (!isConstantSelection(selectedSingleValue)) return
    const display = selectedSingleValue.displayValue ?? selectedSingleValue.value
    if (isDateHeader) {
      const date = display
        ? moment(display, 'DD/MM/YYYY', true).isValid()
          ? moment(display, 'DD/MM/YYYY')
          : moment(selectedSingleValue.value)
        : null
      setConstantDate(date && date.isValid() ? date : null)
    } else {
      setConstantText(selectedSingleValue.value || '')
    }
  }, [isConstantSelected, selectedSingleValue, isDateHeader])

  useEffect(() => {
    if (!providedConstantOptions || providedConstantOptions.length === 0) return
    const ensureInOptions = (val: string, opts: string[]) => {
      if (!val) return ''
      return opts.includes(val) ? val : ''
    }
    setConstantText(prev => ensureInOptions(prev, providedConstantOptions))
  }, [providedConstantOptions])

  const mappedComplete = useMemo(() => {
    if (multiple) {
      return selections.length > 0
    }
    if (!selectedSingleValue) return false
    if (selectedSingleValue.type === 'composite') return true
    if (selectedSingleValue.type === 'constant') {
      return Boolean((selectedSingleValue.displayValue ?? selectedSingleValue.value)?.toString().trim())
    }
    return true
  }, [multiple, selections, selectedSingleValue])

  const headerSelections = selections.filter(isHeaderSelection)

  const constantAutocompleteOption = useMemo(
    () =>
      !multiple
        ? ({
          name: 'Constant value…',
          label: 'Constant value…',
          status: 'additional',
          fieldType: 'constant',
          fieldStructure: 'constant',
          isConstantOption: true,
        } as any)
        : null,
    [multiple]
  )

  const autocompleteOptions = useMemo(
    () =>
      (!multiple && constantAutocompleteOption
        ? [...options, constantAutocompleteOption]
        : options) as any[],
    [multiple, options, constantAutocompleteOption]
  )

  const autocompleteValue = useMemo(() => {
    if (multiple) {
      return options.filter(opt => headerSelections.some(sel => sel.value === opt.name))
    }
    if (isCompositeMapped) return null
    if (isConstantSelected || localConstSelected) return constantAutocompleteOption
    if (selectedSingleValue?.type === 'header') {
      return options.find(opt => opt.name === selectedSingleValue.value) ?? null
    }
    return null
  }, [multiple, options, headerSelections, isCompositeMapped, isConstantSelected, localConstSelected, selectedSingleValue, constantAutocompleteOption])

  const buildConstantSelection = (value: string, displayValue?: string): MappingSelection[] => [createConstantSelection(value, displayValue)]

  const constantOptionsForField: string[] | undefined =
    providedConstantOptions !== undefined ? providedConstantOptions ?? [] : undefined

  const handleConstantOptionSelection = (selectedName: string) => {
    setConstantText(selectedName)
    handleChange(header, buildConstantSelection(selectedName, selectedName))
    setLocalConstSelected(false)
  }

  const gaiaFieldMeta = (fileUpload.options || []).find(option => option.name === header)

  const minDate = fileUpload.minDate ? moment(fileUpload.minDate) : null
  const maxDate = fileUpload.maxDate ? moment(fileUpload.maxDate) : null

  return (
    <Card sx={{
      height: '100%', boxShadow: 'none', borderWidth: '1px', borderStyle: 'solid', borderColor: mappedComplete ? 'primary.main' : 'lightGrey.dark', borderRadius: '10px', backgroundColor: mappedComplete ? 'secondary.light' : 'common.white', cursor: 'default',
    }}>
      <CardContent sx={{ height: '100%', position: 'relative' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
            <Typography variant='subtitle1'>{header}</Typography>
            {gaiaFieldMeta?.tooltip && (
              <TooltipButton
                text={gaiaFieldMeta?.tooltip}
                noPadding
                placement='right'
                textColor='primary.dark'
              />
            )}
            {mappedComplete && (
              <CheckCircleOutlineOutlined color='primary' />
            )}
          </Stack>
        </Box>
        <FormControl fullWidth variant='outlined' sx={{ mt: 0 }}>
          <Autocomplete
            multiple={multiple}
            options={autocompleteOptions}
            value={autocompleteValue as any}
            disabled={isCompositeMapped}
            getOptionLabel={(option) => (option as IFileUploadMappedOptions).name}
            isOptionEqualToValue={(option, value) => {
              const optName = (option as any).name
              const valName = (value as any).name
              return optName === valName
            }}
            renderTags={(value, getTagProps) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {value.map((option, index) => {
                  const opt = option as IFileUploadMappedOptions
                  const tagProps = getTagProps({ index })
                  return <Chip {...tagProps} label={opt.name} />
                })}
              </Box>
            )}
            onChange={(_, newValue) => {
              if (multiple) {
                const values = (newValue as IFileUploadMappedOptions[]).map(v => v.name)
                handleChange(header, values.map(createHeaderSelection))
                return
              }
              const val = newValue as any
              if (!val) {
                setLocalConstSelected(false)
                handleChange(header, [])
                return
              }
              if (val.isConstantOption) {
                setLocalConstSelected(true)
                if (isDateHeader && (minDate || maxDate)) {
                  const baseDate =
                    headerKey.includes('start') && minDate
                      ? minDate
                      : headerKey.includes('end') && maxDate
                        ? maxDate
                        : null
                  if (baseDate && baseDate.isValid()) {
                    setConstantDate(baseDate)
                    const formatted = baseDate.format('DD/MM/YYYY')
                    handleChange(header, buildConstantSelection(formatted, formatted))
                    return
                  }
                }
                handleChange(header, [])
                return
              }
              setLocalConstSelected(false)
              handleChange(header, [createHeaderSelection(val.name)])
            }}
            renderInput={(params) => (
              /* The card title is the SYSTEM field; this dropdown lists the user's own spreadsheet
                 columns (every caller passes the file's headings as optionsOverride), so it is
                 labelled as their column, not as a system field. */
              <TextField
                {...params}
                label="Your spreadsheet column"
                placeholder={
                  isCompositeMapped
                    ? 'Mapped via composite field'
                    : multiple
                      ? 'Select the columns from your file'
                      : 'Select the column from your file'
                }
              />
            )}
          />
        </FormControl>

        {!multiple && !isCompositeMapped && (isConstantSelected || localConstSelected) && (
          <Box sx={{ mt: 2 }}>
            {constantOptionsForField ? (
              <FormControl fullWidth>
                <Autocomplete
                  options={constantOptionsForField}
                  value={constantText || ''}
                  onChange={(_, newValue) => {
                    const selectedName = String(newValue || '')
                    handleConstantOptionSelection(selectedName)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Select ${header}`}
                      placeholder={`Select ${header}`}
                    />
                  )}
                />
              </FormControl>
            ) : isDateHeader ? (
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    value={constantDate}
                    minDate={minDate ?? undefined}
                    maxDate={maxDate ?? undefined}
                    onChange={(val: any) => {
                      setConstantDate(val)
                      const formatted = val && moment(val).isValid() ? moment(val).format('DD/MM/YYYY') : ''
                      handleChange(header, buildConstantSelection(formatted, formatted))
                      setLocalConstSelected(false)
                    }}
                    format='DD/MM/YYYY'
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label={`Constant ${header}`}
                value={constantText}
                onChange={(e) => {
                  const txt = e.target.value
                  setConstantText(txt)
                  handleChange(header, buildConstantSelection(txt, txt))
                }}
              />
            )}
          </Box>
        )}
        {!multiple && !localConstSelected && selectedSingleValue && selectedSingleValue.type === 'composite' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>Mapped via composite field</Typography>
          </Box>
        )}
      </CardContent>
    </Card >
  )
}

